import {
    DIMENSIONS, LEAKAGES, OBJECTIONS, OUTCOMES, RATING_BANDS, URGENCY_LEVELS,
    STRONG_CALL_THRESHOLD, CRITICAL_CALL_THRESHOLD,
} from "../data/scorecard";

// Rounded before every threshold comparison so e.g. 0.6999999999 (float
// noise from summing weights) isn't read as "below 70%".
const normalize = (value) => Math.round(value * 1e6) / 1e6;

export const weightedScore = (score, weight) => (score === null || score === undefined ? null : (score / 3) * weight);

export function getBand(total) {
    const value = normalize(total);
    return RATING_BANDS.find((band) => value >= band.min) || RATING_BANDS[RATING_BANDS.length - 1];
}

// Live totals for the notepad. Unscored dimensions contribute nothing, like
// the spreadsheet's SUM over blank cells.
export function computeTotals(scores) {
    let total = 0;
    let scoredCount = 0;
    const lost = [];

    DIMENSIONS.forEach((dimension) => {
        const score = scores[dimension.id];
        const weighted = weightedScore(score, dimension.weight);
        if (weighted === null) return;
        total += weighted;
        scoredCount += 1;
        lost.push({ dimension, points: dimension.weight - weighted });
    });

    return {
        total: normalize(total),
        scoredCount,
        band: scoredCount ? getBand(total) : null,
        // where the most points are being lost, biggest first
        biggestLosses: lost.filter((item) => item.points > 0).sort((a, b) => b.points - a.points).slice(0, 3),
    };
}

// The dimension losing the most points maps to a "primary coaching leakage".
export function suggestLeakage(scores) {
    const { biggestLosses } = computeTotals(scores);
    return biggestLosses.length ? biggestLosses[0].dimension.leakage : "";
}

export function getMissingItems(draft) {
    const missing = [];
    if (!draft.info.agent.trim()) missing.push("Agent");
    if (!draft.info.outcome) missing.push("Outcome");
    const unscored = DIMENSIONS.filter((dimension) => draft.scores[dimension.id] === null || draft.scores[dimension.id] === undefined).length;
    if (unscored) missing.push(`${unscored} dimension${unscored > 1 ? "s" : ""} to score`);
    return missing;
}

export function buildEvaluationRecord(draft) {
    const { total } = computeTotals(draft.scores);
    return {
        id: `eval-${Date.now()}`,
        date: draft.info.date,
        team: draft.info.team.trim(),
        manager: draft.info.manager.trim(),
        agent: draft.info.agent.trim(),
        callId: draft.info.callId.trim(),
        geo: draft.info.geo.trim(),
        duration: draft.info.duration.trim(),
        outcome: draft.info.outcome,
        salesScore: total,
        dimensionScores: { ...draft.scores },
        flagsRaised: Object.values(draft.flags).filter((flag) => flag.observed === "Yes" || flag.observed === "Partially").length,
        ...draft.summary,
    };
}

const share = (count, total) => (total ? count / total : 0);

const countBy = (items, field, categories) => {
    const counts = Object.fromEntries(categories.map((category) => [category, 0]));
    items.forEach((item) => {
        if (counts[item[field]] !== undefined) counts[item[field]] += 1;
    });
    return counts;
};

const toMix = (counts, total) =>
    Object.entries(counts).map(([label, count]) => ({ label, count, share: share(count, total) }));

const startOfWeek = (date) => {
    const result = new Date(date);
    result.setHours(0, 0, 0, 0);
    result.setDate(result.getDate() - ((result.getDay() + 6) % 7));
    return result;
};

// Everything the Statistics tab shows, from the evaluation log.
export function computeStats(log) {
    const total = log.length;
    const scores = log.map((item) => item.salesScore);
    const average = total ? scores.reduce((sum, value) => sum + value, 0) / total : 0;

    const bandCounts = Object.fromEntries(RATING_BANDS.map((band) => [band.key, 0]));
    scores.forEach((score) => { bandCounts[getBand(score).key] += 1; });

    const dimensionAverages = DIMENSIONS.map((dimension) => {
        const values = log.map((item) => item.dimensionScores?.[dimension.id]).filter((value) => value !== null && value !== undefined);
        const mean = values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0;
        return { dimension, average: mean, ratio: mean / 3 };
    });

    const weeks = new Map();
    log.forEach((item) => {
        const key = startOfWeek(item.date).getTime();
        weeks.set(key, [...(weeks.get(key) || []), item.salesScore]);
    });
    const trend = [...weeks.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([time, values]) => ({ time, average: values.reduce((sum, value) => sum + value, 0) / values.length, count: values.length }));

    return {
        total,
        average,
        averageBand: total ? getBand(average) : null,
        strongRate: share(scores.filter((score) => normalize(score) >= STRONG_CALL_THRESHOLD).length, total),
        criticalRate: share(scores.filter((score) => normalize(score) < CRITICAL_CALL_THRESHOLD).length, total),
        bandCounts,
        outcomeMix: toMix(countBy(log, "outcome", OUTCOMES), total),
        leakageMix: toMix(countBy(log, "leakage", LEAKAGES), total).sort((a, b) => b.count - a.count),
        objectionMix: toMix(countBy(log, "objection", OBJECTIONS), total).sort((a, b) => b.count - a.count),
        urgencyMix: toMix(countBy(log, "urgency", URGENCY_LEVELS), total),
        dimensionAverages: dimensionAverages.sort((a, b) => a.ratio - b.ratio),
        trend,
        recent: [...log].sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : b.id.localeCompare(a.id))).slice(0, 8),
    };
}

export function filterByPeriod(log, days) {
    if (!days) return log;
    const limit = new Date();
    limit.setHours(0, 0, 0, 0);
    limit.setDate(limit.getDate() - days);
    const limitKey = limit.toISOString().slice(0, 10);
    return log.filter((item) => item.date >= limitKey);
}

export const formatPercent = (ratio, digits = 0) => `${(ratio * 100).toFixed(digits)}%`;
